import { useState, Suspense } from 'react';
import { Button, Tooltip } from '@mui/material';
import { useLocalization } from '@/hooks/useLocalization';
import { useSelector } from 'react-redux';
import type { WARootState } from '@/store/store';
import { useModuleAccess } from '../hooks/useModuleAccess';
import type { ModuleDefinition } from '../types';

interface ModuleActionButtonsProps {
  /** Module definitions to render buttons for */
  definitions: Record<string, ModuleDefinition>;
  variant?: 'outlined' | 'contained' | 'text';
  onActionTriggered?: (moduleKey: string, actionType: string) => void;
  /**
   * Optional visibility predicate for module definitions.
   * If omitted, all definitions are considered visible.
   */
  isModuleVisible?: (module: ModuleDefinition) => boolean;
  /** Optional label override for a module button. */
  getModuleLabel?: (module: ModuleDefinition) => string;
}

/**
 * Renders action buttons for all defined modules.
 *
 * Shows all modules from definitions. Modules that are restricted or not yet
 * implemented are shown as disabled with an explanatory tooltip.
 *
 * @example
 * ```tsx
 * import { quickLinkModuleDefinitions } from "@/permissions";
 *
 * <ModuleActionButtons
 *   definitions={quickLinkModuleDefinitions}
 *   variant="outlined"
 * />
 * ```
 */
export const ModuleActionButtons = ({
  definitions,
  variant = 'outlined',
  onActionTriggered,
  isModuleVisible,
  getModuleLabel,
}: ModuleActionButtonsProps) => {
  const { t } = useLocalization();
  const { isModuleEnabled, isModuleImplemented } = useModuleAccess();
  const token = useSelector((s: WARootState) => s.appView?.UserInfo?.Token);

  const [openModal, setOpenModal] = useState<string | null>(null);

  // Get all modules with their enabled/implemented status
  const allModules = Object.values(definitions)
    .filter((module) => (isModuleVisible ? isModuleVisible(module) : true))
    .map((module) => ({
      ...module,
      isEnabled: isModuleEnabled(module.moduleKey),
      isImplemented: isModuleImplemented(module),
    }));

  if (allModules.length === 0) {
    return null;
  }

  const handleAction = (module: ModuleDefinition) => {
    onActionTriggered?.(module.moduleKey, module.actionType);

    switch (module.actionType) {
      case 'sso':
        if (module.ssoPath) {
          // Build SSO URL following legacy pattern
          const ssoUrl = `${window.location.origin}${module.ssoPath}?token=${encodeURIComponent(token || '')}`;
          window.open(ssoUrl, '_blank');
        }
        break;

      case 'link':
        if (module.linkUrl) {
          window.open(module.linkUrl, '_blank', 'noopener,noreferrer');
        }
        break;

      case 'modal':
        if (module.modalComponent) {
          setOpenModal(module.moduleKey);
        }
        break;

      case 'custom':
        // Custom handlers can be implemented as needed
        break;
    }
  };

  return (
    <>
      {allModules.map((module) => {
        const IconComponent = module.icon || null;
        const isDisabled = !module.isEnabled || !module.isImplemented;

        // Determine tooltip message
        const getTooltipMessage = () => {
          if (!module.isEnabled) {
            return t(
              'featureNotAvailable',
              'This feature is not available for your account.',
            );
          }
          if (!module.isImplemented) {
            return t(
              'demoFeatureUnavailable',
              "This feature isn't available in the demo. It's planned for a later release.",
            );
          }
          return '';
        };

        return (
          <Tooltip
            key={module.moduleKey}
            title={getTooltipMessage()}
            placement='top'
          >
            <Button
              key={module.moduleKey}
              startIcon={IconComponent ? <IconComponent /> : undefined}
              variant={variant}
              onClick={() => !isDisabled && handleAction(module)}
              disabled={isDisabled}
              sx={{
                width: '100%',
                '&.Mui-disabled': {
                  pointerEvents: 'auto',
                },
              }}
            >
              {getModuleLabel ? getModuleLabel(module) : t(module.titleKey)}
            </Button>
          </Tooltip>
        );
      })}

      {/* Render modal if open */}
      {allModules.map((module) => {
        if (
          module.actionType === 'modal' &&
          module.modalComponent &&
          openModal === module.moduleKey
        ) {
          const ModalComponent = module.modalComponent;
          return (
            <Suspense
              key={`modal-${module.moduleKey}`}
              fallback={null}
            >
              <ModalComponent
                open={true}
                onClose={() => setOpenModal(null)}
              />
            </Suspense>
          );
        }
        return null;
      })}
    </>
  );
};

export default ModuleActionButtons;
